import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { Box, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { SectionCard } from '~/components/ShiftRequestRedesign/common';
import { SelectedDatePill } from '~/components/ShiftRequestRedesign/SelectDates/SelectedDatePill';
import { OutlinePlusIcon } from '@tempmee/react-icons';

interface ISelectedDatesCardProps {
  dates: Date[];
  onRemoveDate: (date: Date) => void;
  onAddDates: () => void;
}

const MAX_VISIBLE_DATES = 10;

export const SelectedDatesCard = ({
  dates,
  onRemoveDate,
  onAddDates,
}: ISelectedDatesCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [dateToRemove, setDateToRemove] = useState<Date | null>(null);

  const sortedDates = useMemo(() => [...dates].sort((a, b) => a.getTime() - b.getTime()), [dates]);
  const visibleDates = expanded ? sortedDates : sortedDates.slice(0, MAX_VISIBLE_DATES);
  const hasOverflow = sortedDates.length > MAX_VISIBLE_DATES;

  const handleConfirmRemove = () => {
    if (dateToRemove) {
      onRemoveDate(dateToRemove);
      setDateToRemove(null);
    }
  };

  return (
    <>
      <SectionCard
        title={`Selected Dates (${dates.length})`}
        testId="selected-dates-card"
        headerRight={
          <Row
            data-testid="add-dates-link"
            data-cy="add-dates-link"
            align="center"
            onClick={onAddDates}
            style={{ cursor: 'pointer', gap: 6 }}
          >
            <OutlinePlusIcon color={colors.secondary[500]} />
            <Typography as="span" bold size="body_large" color={colors.secondary[500]}>
              Add Dates
            </Typography>
          </Row>
        }
      >
        <Box
          style={
            expanded ? { maxHeight: 420, overflow: 'auto', scrollbarWidth: 'thin' } : undefined
          }
        >
          <Row wrap="wrap" style={{ gap: 10 }}>
            {visibleDates.map((date) => (
              <SelectedDatePill
                key={date.toISOString()}
                date={date}
                onRemove={() => setDateToRemove(date)}
              />
            ))}
          </Row>
        </Box>

        {hasOverflow && (
          <Row justify="end" style={{ marginTop: 16 }}>
            <Typography
              as="span"
              data-testid="view-all-link"
              data-cy="view-all-link"
              bold
              size="body_large"
              color={colors.secondary[500]}
              onClick={() => setExpanded(!expanded)}
              style={{ cursor: 'pointer' }}
            >
              View{expanded ? ' Less' : ' All'}
            </Typography>
          </Row>
        )}
      </SectionCard>

      {dateToRemove && (
        <ConfirmPopup
          title="Remove Date"
          description={
            <>
              Are you sure you would like to remove{' '}
              <strong>{format(dateToRemove, 'EEE, MMM dd, yyyy')}</strong>
              <br />
              from your request?
            </>
          }
          leftButtonText="Go Back"
          rightButtonText="Remove"
          leftButtonAction={() => setDateToRemove(null)}
          rightButtonAction={handleConfirmRemove}
          closePopup={() => setDateToRemove(null)}
          dataTestId="remove-date-popup"
          descriptionStyle={{
            width: '85%',
            marginBottom: 32,
          }}
        />
      )}
    </>
  );
};
